import axios from "axios";
import appConfig from "@/config";

const normalizeApiBaseUrl = (value) => {
    const raw = typeof value === "string" ? value.trim() : "";
    if (!raw) return "/";
    const normalized = raw
        .replace(/([^:]\/)\/+/g, "$1");
    return normalized.endsWith("/") ? normalized : `${normalized}/`;
};

const API_BASE_URL = normalizeApiBaseUrl(appConfig.apiBaseUrl || "/");
const API_TIMEOUT = Number(appConfig.apiTimeout) || 15000;

const httpService = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
        "Content-Type": "application/json",
    },
});

let getToken = () => null;
let pendingRequestCount = 0;
const HTTP_PENDING_EVENT = "http:pending";
const UNKNOWN_ERROR = "An unexpected error occurred";
const UNKNOWN_STATUS_TEXT = "Unknown Error";
const MAX_ERROR_SCAN_NODES = 64;
const PRIORITY_KEYS = ["message", "error", "detail", "title", "errors", "data", "response", "result"];
const PRIORITY_KEY_SET = new Set(PRIORITY_KEYS);

const emitPendingRequestCount = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent(HTTP_PENDING_EVENT, { detail: { count: pendingRequestCount } })
    );
};

const incrementPendingRequests = () => {
    pendingRequestCount += 1;
    emitPendingRequestCount();
};

const decrementPendingRequests = () => {
    pendingRequestCount = Math.max(0, pendingRequestCount - 1);
    emitPendingRequestCount();
};

const pickText = (value) =>
    typeof value === "string" && value.trim() ? value.trim() : null;

const tryParseJsonString = (value) => {
    if (typeof value !== "string") return null;
    const input = value.trim();
    if (!input) return null;
    if (!(input.startsWith("{") || input.startsWith("["))) return null;
    try {
        return JSON.parse(input);
    } catch {
        return null;
    }
};

const extractApiMessage = (apiData) => {
    if (apiData == null) return null;

    const rootText = pickText(apiData);
    if (rootText) return rootText;

    let root = apiData;
    if (typeof apiData === "string") {
        const parsed = tryParseJsonString(apiData);
        if (parsed == null) return null;
        root = parsed;
    }
    if (root == null || typeof root !== "object") return null;

    const direct =
        pickText(root.message) ||
        pickText(root.error) ||
        pickText(root.detail) ||
        pickText(root.title) ||
        pickText(root?.error?.message);
    if (direct) return direct;

    const queue = [root];
    const seen = new WeakSet();
    let index = 0;
    let visited = 0;

    while (index < queue.length && visited < MAX_ERROR_SCAN_NODES) {
        const current = queue[index++];
        visited += 1;

        const currentText = pickText(current);
        if (currentText) return currentText;

        if (Array.isArray(current)) {
            for (let i = 0; i < current.length; i++) {
                const item = current[i];
                const itemText = pickText(item);
                if (itemText) return itemText;
                const parsedItem = tryParseJsonString(item);
                if (parsedItem && typeof parsedItem === "object" && !seen.has(parsedItem)) {
                    seen.add(parsedItem);
                    queue.push(parsedItem);
                } else if (item && typeof item === "object" && !seen.has(item)) {
                    seen.add(item);
                    queue.push(item);
                }
            }
            continue;
        }

        if (!current || typeof current !== "object" || seen.has(current)) continue;
        seen.add(current);

        for (let i = 0; i < PRIORITY_KEYS.length; i++) {
            const key = PRIORITY_KEYS[i];
            if (!(key in current)) continue;
            const value = current[key];
            const text = pickText(value);
            if (text) return text;
            const parsedValue = tryParseJsonString(value);
            if (parsedValue && typeof parsedValue === "object" && !seen.has(parsedValue)) {
                seen.add(parsedValue);
                queue.push(parsedValue);
            } else if (value && typeof value === "object" && !seen.has(value)) {
                seen.add(value);
                queue.push(value);
            }
        }

        const keys = Object.keys(current);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (PRIORITY_KEY_SET.has(key)) continue;
            const value = current[key];
            const text = pickText(value);
            if (text) return text;
            const parsedValue = tryParseJsonString(value);
            if (parsedValue && typeof parsedValue === "object" && !seen.has(parsedValue)) {
                seen.add(parsedValue);
                queue.push(parsedValue);
            } else if (value && typeof value === "object" && !seen.has(value)) {
                seen.add(value);
                queue.push(value);
            }
        }
    }

    return null;
};

export const setTokenProvider = (provider) => {
    getToken = typeof provider === "function" ? provider : () => null;
};

export const getHttpPendingCount = () => pendingRequestCount;
export const HTTP_PENDING_COUNT_EVENT = HTTP_PENDING_EVENT;

// Request interceptor
httpService.interceptors.request.use(
    (config) => {
        incrementPendingRequests();
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
httpService.interceptors.response.use(
    (response) => {
        decrementPendingRequests();
        return response.data;
    },
    (error) => {
        decrementPendingRequests();
        const status = error?.response?.status;
        const statusText = error?.response?.statusText;

        if (status === 401 || status === 403) {
            window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        }

        const apiData = error?.response?.data;
        let message = extractApiMessage(apiData);

        if (!message) {
            message =
                error?.message ||
                (status ? `${status}${statusText ? ` ${statusText}` : ""}` : null) ||
                statusText ||
                UNKNOWN_ERROR;
        }

        // Always return a normal response shape to callers (no throw/reject).
        if (apiData != null) {
            if (typeof apiData === "object") {
                return {
                    success: false,
                    status_code: status || apiData?.status_code || 0,
                    message,
                    ...apiData,
                };
            }
            return {
                success: false,
                status_code: status || 0,
                statusText: statusText || UNKNOWN_STATUS_TEXT,
                message,
                data: apiData,
            };
        }

        return {
            success: false,
            status_code: status || 0,
            statusText: statusText || UNKNOWN_STATUS_TEXT,
            message,
            data: null,
        };
    }
);

export default httpService;

export const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    return error?.message || "An error occurred";
};

export const responseError = getErrorMessage;
